import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';

import { User } from '@core/models/auth';
import { AuthMe } from '@core/models/auth';
import { CookieService } from 'ngx-cookie-service';
import { TokenService } from '@/app/core/services/config/token.service';

export type LoginResult =
  | { kind: 'success'; user: User }
  | { kind: 'totp_challenge'; tempToken: string };

export interface TotpStatus {
  totpEnabled: boolean;
  totpVerified: boolean;
  backupCodesRemaining: number;
}

export interface TotpSetupResponse {
  secret: string;
  qrCode: string; // data:image/png;base64,... — usar directo en <img [src]>
  message: string;
}

export interface TotpDisableResponse {
  message: string;
}

export interface TotpConfirmSetupResponse {
  backupCodes: string[];
}

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  user: User | null = null;

  public readonly authSessionKey = 'SessionToken';

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private tokenService: TokenService,
  ) {
    const existing = this.cookieService.get(this.authSessionKey);
    if (existing) {
      this.tokenService.access = existing;
    }
  }

  login(username: string, password: string) {
    return this.http.post<any>(`/auth/login`, { username, password }).pipe(
      map((resp): LoginResult => {
        if (resp?.access_token) {
          // Login normal — guarda el token real
          this.user = resp as User;
          this.saveUserSession(resp as User);
          return { kind: 'success', user: resp as User };
        } else if (resp?.tempToken) {
          // Login con TOTP pendiente.
          // Guardamos el tempToken SOLO para poder pegarle al endpoint
          // de verificación TOTP — NO sirve para /auth/me ni rutas normales.
          this.saveSession(resp.tempToken);
          return { kind: 'totp_challenge', tempToken: resp.tempToken };
        }
        // Caso raro / fallback: lo tratamos como éxito para no romper nada existente
        return { kind: 'success', user: resp as User };
      })
    );
  }

  verifyTotp(code: string) {
    // El tempToken se guardó con saveSession() en el paso 1 del login.
    // El endpoint /auth/totp/validate es @Public() y espera el tempToken
    // como campo del body (no como header Authorization), así que lo
    // leemos de la cookie y lo mandamos explícito.
    const tempToken = this.cookieService.get(this.authSessionKey);

    return this.http.post<any>(`/auth/totp/validate`, { tempToken, code }).pipe(
      map((resp) => {
        this.user = resp as User;
        this.saveUserSession(resp as User); // acá sí se pisa con el access_token real
        return resp as User;
      })
    );
  }

  /**
   * Login de emergencia con código de respaldo, cuando el cliente
   * perdió el acceso a su app de autenticación. Mismo mecanismo que
   * verifyTotp: el tempToken viaja en el body, no como header.
   */
  useBackupCode(backupCode: string) {
    const tempToken = this.cookieService.get(this.authSessionKey);

    return this.http.post<any>(`/auth/totp/use-backup`, { tempToken, backupCode }).pipe(
      map((resp) => {
        this.user = resp as User;
        this.saveUserSession(resp as User);
        return resp as User;
      })
    );
  }

  /**
   * Consulta si la sesión actual ya tiene TOTP activado.
   * Útil para mostrar u ocultar el botón de "Activar 2FA" en el perfil.
   */
  getTotpStatus() {
    return this.http.get<TotpStatus>(`/auth/totp/status`).pipe(
      map((resp) => resp)
    );
  }

  /**
   * Paso 1 de activación: pide el QR/secret para escanear con
   * Google Authenticator (o similar).
   */
  setupTotp() {
    return this.http.post<TotpSetupResponse>(`/auth/totp/setup`, {}).pipe(
      map((resp) => resp)
    );
  }

  /**
   * Paso 2 de activación: confirma con el primer código generado
   * por la app de autenticación. Si es correcto, el backend activa
   * el TOTP y devuelve los códigos de respaldo (mostrar UNA sola vez).
   */
  confirmTotpSetup(code: string) {
    return this.http.post<TotpConfirmSetupResponse>(`/auth/totp/confirm-setup`, { code }).pipe(
      map((resp) => resp)
    );
  }

  /**
   * Desactiva el TOTP de la cuenta. El backend exige el código actual
   * del autenticador para confirmar que es el dueño de la cuenta.
   */
  disableTotp(code: string) {
    return this.http.post<TotpDisableResponse>(`/auth/totp/disable`, { code }).pipe(
      map((resp) => resp)
    );
  }

  signup(payload: {
    email: string;
    identityCard: string;
    lastName: string;
    name: string;
    password: string;
    phone: string;
    referralCode: string;
  }) {
    return this.http.post<User>(`/customers/register`, payload).pipe(map((user) => user));
  }

  getMe() {
    return this.http.get<AuthMe>(`/auth/me`).pipe(map((me) => me));
  }

  logout(): void {
    this.cookieService.delete(this.authSessionKey, '/');
    this.tokenService.access = null;
    this.user = null;
  }

  get session(): string {
    return this.cookieService.get(this.authSessionKey);
  }

  get userSession(): User | null {
    const raw = this.cookieService.get(this.authSessionKey);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as User;
    } catch {
      return null;
    }
  }

  saveSession(token: string): void {
    this.cookieService.set(this.authSessionKey, token);
    this.tokenService.access = token || null;
  }

  private saveUserSession(user: User): void {
    const token = user.access_token || '';
    this.cookieService.set(this.authSessionKey, token);
    this.tokenService.access = token || null;
  }

  public getToken(): string {
    return this.cookieService.get(this.authSessionKey);
  }

  removeSession(): void {
    this.cookieService.delete(this.authSessionKey, '/');
    this.tokenService.access = null;
  }
}